import { Link } from "react-router-dom";
import { ChevronDown, Menu, X, ArrowRight } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useDemoModal } from "@/context/DemoModalContext";

const Header = () => {
  const { openModal } = useDemoModal();
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const [isTrustCenterOpen, setIsTrustCenterOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const productsRef = useRef<HTMLDivElement>(null);
  const resourcesRef = useRef<HTMLDivElement>(null);
  const trustCenterRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        productsRef.current &&
        !productsRef.current.contains(event.target as Node)
      ) {
        setIsProductsOpen(false);
      }
      if (
        resourcesRef.current &&
        !resourcesRef.current.contains(event.target as Node)
      ) {
        setIsResourcesOpen(false);
      }
      if (
        trustCenterRef.current &&
        !trustCenterRef.current.contains(event.target as Node)
      ) {
        setIsTrustCenterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const productLinks = [
    {
      path: "/validate",
      label: "Meshly Validate",
      description: "AI-powered contract validation",
    },
    {
      path: "/intelligence",
      label: "Meshly Intelligence",
      description: "Cross-system monitoring + auditability",
    },
  ];

  const resourcesLinks = [
    { path: "/about", label: "Team" },
    { path: "/roi-calculator", label: "ROI Calculator" },
    { path: "/support", label: "Support" },
    { path: "/blog", label: "Press Room" },
    { path: "/finance-advisory-board", label: "Community" },
  ];

  return (
    <>
      <nav className="relative w-full bg-gradient-to-br from-primary/5 via-accent/3 to-secondary/5">
        <div className="container mx-auto px-4">
          <div className="flex items-center h-20">
            {/* Logo + Nav together on left */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center gap-2" aria-label="Meshly home">
                <div
                  role="img"
                  aria-label="Meshly"
                  title="Meshly"
                  className="h-14 w-14 bg-[#4B45E5]"
                  style={{
                    WebkitMaskImage: "url(/logo-octopus.png)",
                    maskImage: "url(/logo-octopus.png)",
                    WebkitMaskSize: "contain",
                    maskSize: "contain",
                    WebkitMaskRepeat: "no-repeat",
                    maskRepeat: "no-repeat",
                    WebkitMaskPosition: "center",
                    maskPosition: "center",
                  }}
                />
                <span className="text-xl font-bold tracking-[-0.04em] text-foreground">meshly</span>
              </Link>

              {/* Desktop Navigation - Next to logo */}
              <div className="hidden md:flex items-center space-x-8 ml-12">
                {/* Products Dropdown */}
                <div className="relative" ref={productsRef}>
                  <button
                    className="flex items-center gap-1 text-foreground hover:text-foreground/70 transition-colors font-medium"
                    onClick={() => {
                      setIsProductsOpen(!isProductsOpen);
                      setIsResourcesOpen(false);
                      setIsTrustCenterOpen(false);
                    }}
                  >
                    Products
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${isProductsOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  {/* Always rendered (visibility toggled via CSS, not mount/unmount) so
                      static/pre-rendered HTML always contains these links -- otherwise
                      crawlers never see /validate, /intelligence
                      linked from anywhere and flag them as orphan pages. */}
                  <div
                    className={`absolute left-0 mt-2 w-56 bg-white rounded-lg shadow-xl py-2 border border-gray-100 z-50 ${
                      isProductsOpen ? "block" : "hidden"
                    }`}
                  >
                    {productLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        className="block px-4 py-3 text-gray-800 hover:bg-gray-50 transition-colors"
                        onClick={() => setIsProductsOpen(false)}
                      >
                        <span className="font-medium">{link.label}</span>
                        <p className="text-sm text-gray-500">
                          {link.description}
                        </p>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Pricing */}
                <Link
                  to="/pricing"
                  className="text-foreground hover:text-foreground/70 transition-colors font-medium"
                >
                  Pricing
                </Link>

                {/* Resources Dropdown */}
                <div className="relative" ref={resourcesRef}>
                  <button
                    className="flex items-center gap-1 text-foreground hover:text-foreground/70 transition-colors font-medium"
                    onClick={() => {
                      setIsResourcesOpen(!isResourcesOpen);
                      setIsProductsOpen(false);
                      setIsTrustCenterOpen(false);
                    }}
                  >
                    Resources
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${isResourcesOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  {/* Always rendered (visibility toggled via CSS, not mount/unmount) --
                      same crawlability reasoning as the Products dropdown above. This
                      is what actually links /finance-advisory-board into the site. */}
                  <div
                    className={`absolute left-0 mt-2 min-w-[11rem] w-52 bg-white rounded-lg shadow-xl py-2 border border-gray-100 z-50 ${
                      isResourcesOpen ? "block" : "hidden"
                    }`}
                  >
                    {resourcesLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        className="block px-4 py-2.5 text-gray-800 hover:bg-gray-50 transition-colors"
                        onClick={() => setIsResourcesOpen(false)}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Trust Center Dropdown */}
                <div className="relative" ref={trustCenterRef}>
                  <button
                    className="flex items-center gap-1 text-foreground hover:text-foreground/70 transition-colors font-medium"
                    onClick={() => {
                      setIsTrustCenterOpen(!isTrustCenterOpen);
                      setIsProductsOpen(false);
                      setIsResourcesOpen(false);
                    }}
                  >
                    Trust Center
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${isTrustCenterOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  {isTrustCenterOpen && (
                    <div className="absolute left-0 mt-2 min-w-[11rem] w-52 bg-white rounded-lg shadow-xl py-2 border border-gray-100 z-50">
                      <a
                        href="https://app.vanta.com/meshly.ai/trust/b667n0dd22c1b8xg5yqvf8"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-4 py-2.5 text-gray-800 hover:bg-gray-50 transition-colors"
                        onClick={() => setIsTrustCenterOpen(false)}
                      >
                        Trust Center
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Login & Get Started - Right side */}
            <div className="hidden md:flex items-center gap-4 ml-auto">
              <a
                href="https://validate.meshly.ai"
                className="text-foreground hover:text-foreground/70 transition-colors font-medium"
              >
                Log in
              </a>
              <Button
                className="bg-[#4B45E5] text-white hover:bg-[#4B45E5]/90 font-semibold rounded-full px-6"
                onClick={openModal}
              >
                Get Started
              </Button>
            </div>

            {/* Mobile Menu Button — ml-auto pushes it to the right edge on mobile
                (the desktop Log in / Get Started block that normally holds the
                right side is hidden below md, so without this the hamburger packs
                left next to the logo). */}
            <button
              className="md:hidden ml-auto text-foreground"
              onClick={() => setIsMobileMenuOpen(true)}
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[100] md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsMobileMenuOpen(false)}
          />

          {/* Menu Panel */}
          <div className="absolute inset-x-4 top-4 bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <Link
                to="/"
                className="flex items-center gap-2"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-label="Meshly home"
              >
                <div
                  role="img"
                  aria-label="Meshly"
                  title="Meshly"
                  className="h-10 w-10 bg-[#4B45E5]"
                  style={{
                    WebkitMaskImage: "url(/logo-octopus.png)",
                    maskImage: "url(/logo-octopus.png)",
                    WebkitMaskSize: "contain",
                    maskSize: "contain",
                    WebkitMaskRepeat: "no-repeat",
                    maskRepeat: "no-repeat",
                    WebkitMaskPosition: "center",
                    maskPosition: "center",
                  }}
                />
                <span className="text-xl font-bold tracking-[-0.04em] text-foreground">meshly</span>
              </Link>
              <button
                className="p-2 rounded-full bg-gray-100 text-gray-600"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Products Section */}
            <div className="p-4 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                Products
              </p>
              {productLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="flex items-center justify-between py-3 text-gray-900 hover:text-primary"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <div>
                    <span className="font-medium text-lg">{link.label}</span>
                    <p className="text-sm text-gray-500">{link.description}</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-gray-400" />
                </Link>
              ))}
              <Link
                to="/pricing"
                className="flex items-center justify-between py-3 text-gray-900 hover:text-primary"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="font-medium text-lg">Pricing</span>
                <ArrowRight className="h-5 w-5 text-gray-400" />
              </Link>
            </div>

            {/* Resources Section */}
            <div className="p-4 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                Resources
              </p>
              {resourcesLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="flex items-center justify-between py-3 text-gray-900 hover:text-primary"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span className="font-medium text-lg">{link.label}</span>
                  <ArrowRight className="h-5 w-5 text-gray-400" />
                </Link>
              ))}
            </div>

            {/* Trust Center Section */}
            <div className="p-4 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                Trust Center
              </p>
              <a
                href="https://app.vanta.com/meshly.ai/trust/b667n0dd22c1b8xg5yqvf8"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between py-3 text-gray-900 hover:text-primary"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="font-medium text-lg">Trust Center</span>
                <ArrowRight className="h-5 w-5 text-gray-400" />
              </a>
            </div>

            {/* Buttons */}
            <div className="p-4 flex gap-3">
              <a href="https://validate.meshly.ai" className="flex-1">
                <Button
                  variant="outline"
                  className="w-full rounded-full bg-gray-100 border-0 text-gray-900 hover:bg-gray-200 font-medium"
                >
                  Log In
                </Button>
              </a>
              <Button
                className="flex-1 rounded-full bg-[#86efac] hover:bg-[#86efac]/90 text-gray-900 font-medium"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  openModal();
                }}
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
